import { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import AnalysisResult from "@/components/AnalysisResult";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const ranIntent = useRef(false);

  const runAnalyze = async (target) => {
    const value = (target || "").trim();
    if (!value) return;
    setLoading(true);
    setReport(null);
    try {
      const { data } = await api.post("/analyze", { url: value });
      setReport(data);
      toast.success("Analysis complete", { description: `Score: ${data.score}/100` });
    } catch (e) {
      toast.error("Analysis failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  };

  const analyze = (e) => {
    e.preventDefault();
    runAnalyze(url);
  };

  // Deep link: landing page can send visitors to /?url=<site> (or via sessionStorage after sign-in)
  useEffect(() => {
    if (ranIntent.current) return;
    const fromQuery = new URLSearchParams(window.location.search).get("url");
    const pending = fromQuery || sessionStorage.getItem("sap_pending_url");
    if (pending) {
      ranIntent.current = true;
      sessionStorage.removeItem("sap_pending_url");
      setUrl(pending);
      runAnalyze(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      {!report && !loading && (
        <div className="max-w-2xl mx-auto text-center pt-10 md:pt-20">
          <div className="inline-flex items-center gap-2 uppercase-label mb-4">
            <Sparkles className="w-4 h-4 text-klein" /> AI-Powered SEO Audit
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Analyze any website's SEO
          </h1>
          <p className="text-gray-500 mt-4 text-lg">
            Enter a URL to get an instant on-page, technical & keyword audit with actionable AI recommendations.
          </p>
        </div>
      )}

      <form onSubmit={analyze} className={`${report || loading ? "" : "max-w-2xl mx-auto mt-10"} `}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="analyze-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="example.com"
              className="pl-12 py-6 text-lg border-gray-300 focus:border-black"
            />
          </div>
          <Button
            type="submit"
            data-testid="analyze-url-button"
            disabled={loading}
            className="bg-[#0A0A0A] hover:bg-gray-800 py-6 px-8 text-base"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Analyze"}
          </Button>
        </div>
      </form>

      {loading && (
        <div className="mt-12 flex flex-col items-center justify-center text-center animate-fade-up">
          <Loader2 className="w-10 h-10 animate-spin text-klein" />
          <p className="mt-4 font-heading font-semibold text-lg">Crawling & analyzing…</p>
          <p className="text-gray-500 text-sm">Running checks and generating AI recommendations.</p>
        </div>
      )}

      {report && (
        <div className="mt-8">
          <AnalysisResult report={report} />
        </div>
      )}
    </div>
  );
}
